<template>
  <div class="wl-demo" :style="brandVars">
    <div class="wl-demo__switch" role="tablist" :aria-label="t('pricingPlansDemoToggleLabel')">
      <button
        v-for="mode in modes"
        :key="mode.key"
        type="button"
        role="tab"
        class="wl-demo__switch-btn"
        :class="{ 'wl-demo__switch-btn--active': activeMode === mode.key }"
        :aria-selected="activeMode === mode.key ? 'true' : 'false'"
        @click="activeMode = mode.key"
      >
        <i class="fa-solid" :class="mode.icon" aria-hidden="true"></i>
        {{ t(mode.labelKey) }}
      </button>
    </div>

    <div class="row wl-demo__row">
      <div class="col-12 col-lg-7">
        <!-- Schematic of a real itinerary page; the same markup switches between the two brandings. -->
        <div class="wl-demo__browser" :class="{ 'wl-demo__browser--wl': isWhiteLabel }" aria-hidden="true">
          <div class="wl-demo__chrome">
            <span class="wl-demo__dots"><span></span><span></span><span></span></span>
            <span class="wl-demo__url">
              <i class="fa-solid fa-lock"></i>
              {{ isWhiteLabel ? "mayatours.tripplanner.co.il" : "tripplanner.co.il" }}/ride
            </span>
          </div>

          <div class="wl-demo__page">
            <div class="wl-demo__nav">
              <span class="wl-demo__marker wl-demo__marker--logo">1</span>
              <i v-if="!isWhiteLabel" class="fa-solid fa-bars wl-demo__burger"></i>
              <img
                class="wl-demo__logo"
                :src="isWhiteLabel ? brand.logo : tripPlannerLogo"
                width="156"
                height="37"
                loading="lazy"
                alt=""
              >
              <span v-if="!isWhiteLabel" class="wl-demo__menu">
                <span>{{ t("home") }}</span>
                <span>{{ t("about") }}</span>
                <span>{{ t("myTrips") }}</span>
                <i class="fa-brands fa-facebook"></i>
              </span>
              <span class="wl-demo__marker wl-demo__marker--menu">3</span>
            </div>

            <div class="wl-demo__hero">
              <span class="wl-demo__marker wl-demo__marker--colors">2</span>
              <strong class="wl-demo__trip-title">{{ t("pricingPlansDemoTripTitle") }}</strong>
              <span class="wl-demo__trip-meta">{{ t("pricingPlansDemoTripMeta") }}</span>
            </div>

            <div class="wl-demo__agent">
              <img class="wl-demo__agent-pic" :src="agentImage" width="40" height="40" loading="lazy" alt="">
              <span>
                <strong>{{ t("pricingPlansDemoAgentName") }}</strong>
                <small>{{ t("pricingPlansDemoAgentTitle") }}</small>
              </span>
              <!-- The agency's own links, shown in both modes just like the real agent banner -->
              <span class="wl-demo__agent-links">
                <img v-for="icon in agentLinkIcons" :key="icon" :src="icon" width="24" height="24" loading="lazy" alt="">
              </span>
            </div>

            <ul class="wl-demo__days">
              <li v-for="day in days" :key="day.number" class="wl-demo__day">
                <span class="wl-demo__day-badge">{{ t("pricingPlansDemoDay", { day: day.number }) }}</span>
                <span class="wl-demo__day-name">{{ t(day.nameKey) }}</span>
                <span class="wl-demo__day-btn">
                  <i class="fa-solid fa-location-arrow"></i>
                  {{ t("pricingPlansDemoNavigate") }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-5">
        <p class="wl-demo__caption">
          <span
            class="wl-demo__badge"
            :class="{ 'wl-demo__badge--exclusive': isWhiteLabel }"
          >{{ isWhiteLabel ? t("pricingPlansWhiteLabelBadge", { plan: whiteLabelPlanNames }) : t("pricingPlansBannerBadge") }}</span>
          <strong class="wl-demo__caption-title">
            {{ isWhiteLabel ? t("pricingPlansWhiteLabelTitle") : t("pricingPlansBannerTitle") }}
          </strong>
          {{ isWhiteLabel ? t("pricingPlansWhiteLabelText") : t("pricingPlansBannerText") }}
        </p>

        <ol class="wl-demo__points">
          <li v-for="(point, index) in points" :key="point.titleKey" class="wl-demo__point">
            <span class="wl-demo__point-num" aria-hidden="true">{{ index + 1 }}</span>
            <span>
              <strong class="wl-demo__point-title">
                <i class="fa-solid" :class="point.icon" aria-hidden="true"></i>
                {{ t(point.titleKey) }}
              </strong>
              <span class="wl-demo__point-text">{{ t(point.textKey) }}</span>
            </span>
          </li>
        </ol>

        <p class="wl-demo__example-note">{{ t("pricingPlansDemoExampleNote") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import config from "@/common/config";
import { translationMixin } from "@/mixins/translationMixin";

const MODE_REGULAR = "regular";
const MODE_WHITE_LABEL = "whiteLabel";

// Maya Tours' plain logo, as shown in the agent banner on its itineraries.
const MAYA_TOURS_AGENT_IMAGE = "https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/profile_images%2F1_26_Aug_2026_14%3A06%3A41_GMT?alt=media&token=b68dc1fc-4106-4cb3-b967-912573fc0b96";

// Presentation-only: shows a real white label (Maya Tours) next to the regular TripPlanner branding.
export default {
  name: "WhiteLabelDemo",
  mixins: [translationMixin],
  props: {
    whiteLabelPlanNames: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      activeMode: MODE_WHITE_LABEL,
      brand: config.config.THEMES.mayatours,
      tripPlannerLogo: require("@/assets/images/Logo.svg"),
      agentImage: MAYA_TOURS_AGENT_IMAGE,
      // Same Facebook / Instagram / website icons as PresentUserInfo
      agentLinkIcons: [
        "https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/icons%2FFacebook_Logo_Primary.png?alt=media&token=2731adce-75a0-493d-ac5d-94a2913b2aa4",
        "https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/icons%2FInstagram_Glyph_Gradient.png?alt=media&token=67820264-b94c-48d9-b2fc-e1a6acda27d2",
        "https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/icons%2FURLIcon.png?alt=media&token=010bba69-2e1c-4284-9f13-e6c5e8612cc8",
      ],
      modes: [
        { key: MODE_REGULAR, labelKey: "pricingPlansDemoTabRegular", icon: "fa-id-card" },
        { key: MODE_WHITE_LABEL, labelKey: "pricingPlansDemoTabWhiteLabel", icon: "fa-palette" },
      ],
      days: [
        { number: 1, nameKey: "pricingPlansDemoDay1" },
        { number: 2, nameKey: "pricingPlansDemoDay2" },
        { number: 3, nameKey: "pricingPlansDemoDay3" },
      ],
      points: [
        { titleKey: "pricingPlansDemoPointLogoTitle", textKey: "pricingPlansDemoPointLogoText", icon: "fa-image" },
        { titleKey: "pricingPlansDemoPointColorsTitle", textKey: "pricingPlansDemoPointColorsText", icon: "fa-palette" },
        { titleKey: "pricingPlansDemoPointClosedTitle", textKey: "pricingPlansDemoPointClosedText", icon: "fa-lock" },
      ],
    };
  },
  computed: {
    isWhiteLabel() {
      return this.activeMode === MODE_WHITE_LABEL;
    },
    // The mockup reads its colors from these, so it shows the real theme from config.
    brandVars() {
      const colors = this.brand.colors;
      return {
        "--wl-brand-hero": colors.primaryBackground,
        "--wl-brand-button": colors.secondaryBackground,
        "--wl-brand-accent": colors.primary,
        "--wl-brand-gold": colors.secondary,
        "--wl-brand-day": colors.rideItineraryBackgroundColor,
      };
    },
  },
};
</script>

<style scoped>
/* Root font-size in this app is 10px, so 1.6rem = 16px. */
.wl-demo {
  --wl-tp-hero: linear-gradient(45deg, #34d0b6, #07a0c3);
  --wl-tp-accent: #07a0c3;
  --wl-tp-day: rgba(7, 160, 195, 0.08);
  --wl-line: rgba(7, 160, 195, 0.14);
  --wl-muted: rgba(33, 37, 41, 0.72);
}

/* ---------- Switch ---------- */
.wl-demo__switch {
  display: flex;
  width: fit-content;
  margin: 0 auto 2.8rem;
  padding: 0.4rem;
  gap: 0.4rem;
  background: rgba(7, 160, 195, 0.08);
  border-radius: 999px;
}

.wl-demo__switch-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.9rem 2.2rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: #0b6f86;
  background: transparent;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
}

.wl-demo__switch-btn--active {
  color: #fff;
  background: linear-gradient(45deg, #34d0b6, #07a0c3);
  box-shadow: 0 4px 12px rgba(7, 160, 195, 0.3);
}

.wl-demo__switch-btn:focus-visible {
  outline: 3px solid rgba(7, 160, 195, 0.5);
  outline-offset: 2px;
}

.wl-demo__row {
  row-gap: 2.8rem;
  align-items: center;
}

/* ---------- Browser mockup ---------- */
.wl-demo__browser {
  --hero: var(--wl-tp-hero);
  --accent: var(--wl-tp-accent);
  --button: var(--wl-tp-hero);
  --day: var(--wl-tp-day);
  border-radius: 16px;
  border: 1px solid var(--wl-line);
  background: #fff;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(15, 53, 94, 0.14);
}

.wl-demo__browser--wl {
  --hero: var(--wl-brand-hero);
  --accent: var(--wl-brand-accent);
  --button: var(--wl-brand-button);
  --day: var(--wl-brand-day);
}

.wl-demo__chrome {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.9rem 1.4rem;
  background: #f1f3f5;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.wl-demo__dots {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.wl-demo__dots span {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #d0d4d9;
}

/* Addresses are always written left to right, even on the Hebrew page. */
.wl-demo__url {
  direction: ltr;
  flex-grow: 1;
  min-width: 0;
  padding: 0.4rem 1.2rem;
  font-size: 1.25rem;
  color: #495057;
  background: #fff;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wl-demo__url i {
  font-size: 1rem;
  color: #2b9348;
  margin-inline-end: 0.4rem;
}

.wl-demo__page {
  position: relative;
}

.wl-demo__nav {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.4rem;
  min-height: 6rem;
  /* Room on both edges for the 1 and 3 markers, so they never cover the hamburger or the Facebook icon. */
  padding: 0.8rem 3.2rem;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

.wl-demo__burger {
  font-size: 1.6rem;
  color: #6c757d;
}

/* A bit larger than the real 37px navbar logo, so the agency logo reads clearly in the demo. */
.wl-demo__logo {
  height: 4.4rem;
  width: auto;
  max-width: 18.6rem;
  object-fit: contain;
}

.wl-demo__menu {
  display: flex;
  align-items: center;
  gap: 1.4rem;
  margin-inline-start: auto;
  font-size: 1.3rem;
  font-weight: 600;
  color: #495057;
}

.wl-demo__menu .fa-facebook {
  font-size: 1.6rem;
  color: #1877f2;
}

.wl-demo__hero {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 2.2rem 2rem;
  color: #fff;
  background: var(--hero);
  transition: background 0.4s ease;
}

.wl-demo__trip-title {
  font-size: 2rem;
  font-weight: 800;
}

.wl-demo__trip-meta {
  font-size: 1.3rem;
  opacity: 0.9;
}

.wl-demo__agent {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1.4rem 1.6rem 0;
  padding: 0.8rem 1.2rem;
  border: 1px solid var(--wl-line);
  border-radius: 12px;
}

.wl-demo__agent-pic {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.wl-demo__agent-links {
  display: flex;
  gap: 0.6rem;
  flex-shrink: 0;
  margin-inline-start: auto;
}

.wl-demo__agent-links img {
  width: 2.4rem;
  height: 2.4rem;
  object-fit: contain;
}

.wl-demo__agent strong,
.wl-demo__agent small {
  display: block;
  line-height: 1.3;
}

.wl-demo__agent strong {
  font-size: 1.4rem;
}

.wl-demo__agent small {
  font-size: 1.2rem;
  color: var(--wl-muted);
}

.wl-demo__days {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin: 0;
  padding: 1.4rem 1.6rem 1.8rem;
  list-style: none;
}

.wl-demo__day {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.9rem 1.2rem;
  background: var(--day);
  border-radius: 10px;
  transition: background 0.4s ease;
}

.wl-demo__day-badge {
  flex-shrink: 0;
  padding: 0.3rem 0.9rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #fff;
  background: var(--accent);
  border-radius: 6px;
  transition: background 0.4s ease;
}

.wl-demo__day-name {
  flex-grow: 1;
  min-width: 0;
  font-size: 1.4rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.wl-demo__day-btn {
  flex-shrink: 0;
  padding: 0.4rem 1rem;
  font-size: 1.2rem;
  font-weight: 600;
  color: #fff;
  background: var(--button);
  border-radius: 8px;
  transition: background 0.4s ease;
}

/* Numbered markers tie the mockup to the numbered points beside it. */
.wl-demo__marker {
  position: absolute;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.2rem;
  height: 2.2rem;
  font-size: 1.2rem;
  font-weight: 800;
  color: #1e1e1e;
  background: #f6b73f;
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.wl-demo__marker--logo {
  top: 0.4rem;
  inset-inline-start: 0.4rem;
}

.wl-demo__marker--menu {
  top: 50%;
  transform: translateY(-50%);
  inset-inline-end: 0.6rem;
}

.wl-demo__marker--colors {
  top: 0.8rem;
  inset-inline-end: 0.8rem;
}

/* ---------- Explanation ---------- */
.wl-demo__caption {
  margin: 0 0 2rem;
  font-size: 1.5rem;
  line-height: 1.65;
  color: var(--wl-muted);
}

.wl-demo__caption-title {
  display: block;
  margin-bottom: 0.4rem;
  font-size: 2.2rem;
  font-weight: 800;
  color: #1e1e1e;
}

.wl-demo__badge {
  display: inline-block;
  margin-bottom: 1rem;
  padding: 0.4rem 1.2rem;
  font-size: 1.3rem;
  font-weight: 700;
  color: #0b6f86;
  background: rgba(7, 160, 195, 0.06);
  border-radius: 999px;
}

.wl-demo__badge--exclusive {
  color: #fff;
  background: linear-gradient(45deg, #34d0b6, #07a0c3);
}

.wl-demo__points {
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.wl-demo__point {
  display: flex;
  gap: 1.2rem;
  align-items: flex-start;
}

.wl-demo__point-num {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.8rem;
  height: 2.8rem;
  font-size: 1.4rem;
  font-weight: 800;
  color: #1e1e1e;
  background: #f6b73f;
  border-radius: 50%;
}

.wl-demo__point-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.7rem;
  font-weight: 800;
}

.wl-demo__point-title i {
  color: #07a0c3;
}

.wl-demo__point-text {
  display: block;
  font-size: 1.5rem;
  line-height: 1.6;
  color: var(--wl-muted);
}

.wl-demo__example-note {
  margin: 2rem 0 0;
  font-size: 1.3rem;
  color: var(--wl-muted);
}

@media (max-width: 575.98px) {
  .wl-demo__switch-btn {
    padding: 0.8rem 1.6rem;
    font-size: 1.4rem;
  }

  .wl-demo__menu span {
    display: none;
  }

  .wl-demo__day-btn {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wl-demo__hero,
  .wl-demo__day,
  .wl-demo__day-badge,
  .wl-demo__day-btn,
  .wl-demo__switch-btn {
    transition: none;
  }
}
</style>
